import React, { useEffect, useState } from 'react';
import css from './ReportPage.module.css';

/**
 * ReportPage Component
 * 
 * SECURITY: This component displays the Dorset debug matching report.
 * Access to this page is controlled by M365 group membership ("Internal Portal Reports" group).
 * Only users in that group will see the Report tab in the navigation.
 * 
 * The report is displayed in a white card panel matching the Users page style.
 * The report is loaded via iframe from /dorset-report.html, which is served by nginx
 * directly (not processed by React Router). This ensures:
 * 1. The report's inline CSS and JavaScript work correctly
 * 2. No conflicts with React's CSS/JS
 * 3. The report remains a standalone, self-contained HTML file
 * 
 * The report HTML is generated by Debug_Matching.py and contains:
 * - Summary statistics of job matching
 * - Detailed job summary table with filtering and sorting
 * - Expandable rows showing candidate matching details
 */
const ReportPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const iframeRef = React.useRef(null);

  useEffect(() => {
    // SECURITY: Set loading state when iframe loads
    // This provides user feedback while the report loads
    let timeoutId = null;
    
    const handleLoad = () => {
      if (timeoutId) clearTimeout(timeoutId);
      setIsLoading(false);
      setHasError(false);
      
      // SECURITY: Hide the top header section in the iframe content
      // This removes the "Debug Matching Report" title and description section
      // Only works if iframe is same-origin (which it is in this case)
      try {
        const iframe = iframeRef.current;
        if (iframe && iframe.contentDocument) {
          const iframeDoc = iframe.contentDocument;
          
          // Method 1: Hide the first h1 that contains "Debug Matching Report"
          const h1Elements = iframeDoc.querySelectorAll('h1');
          h1Elements.forEach(h1 => {
            if (h1.textContent.includes('Debug Matching Report')) {
              // Hide the h1 element
              h1.style.display = 'none';
              
              // Hide the following paragraph with description (but NOT the "Generated:" timestamp)
              let nextElement = h1.nextElementSibling;
              while (nextElement) {
                const text = nextElement.textContent || '';
                // SECURITY: Only hide the description paragraph, keep the "Generated:" timestamp visible
                if (text.includes('detailed analysis') || text.includes('job matching results')) {
                  nextElement.style.display = 'none';
                  break;
                }
                // SECURITY: Don't hide paragraphs that contain "Generated:" - this is the timestamp we want to keep
                if (text.includes('Generated:')) {
                  // Keep this visible - it's the timestamp
                  break;
                }
                // Also check if it's a horizontal rule or separator
                if (nextElement.tagName === 'HR' || nextElement.tagName === 'hr') {
                  nextElement.style.display = 'none';
                }
                nextElement = nextElement.nextElementSibling;
              }
              
              // Check parent container - if it's a div that only contains header content, hide it
              const parent = h1.parentElement;
              if (parent) {
                const parentText = parent.textContent || '';
                // If parent contains the header text and description, check if we should hide the whole container
                if (parentText.includes('Debug Matching Report') && 
                    parentText.includes('detailed analysis')) {
                  // Count how many children are header-related
                  const children = Array.from(parent.children);
                  const headerChildren = children.filter(child => {
                    const childText = child.textContent || '';
                    return childText.includes('Debug Matching Report') || 
                           childText.includes('detailed analysis') ||
                           childText.includes('job matching results');
                  });
                  // If most or all children are header-related, hide the container
                  if (headerChildren.length >= children.length * 0.8) {
                    parent.style.display = 'none';
                  }
                }
              }
            }
          });
          
          // Method 2: Hide any element containing the description text
          const allElements = iframeDoc.querySelectorAll('*');
          allElements.forEach(element => {
            const text = element.textContent || '';
            if (text.trim() === 'This report shows detailed analysis of job matching results, including matched candidates, unmatched jobs, and reasons for matching or lack of matches.') {
              element.style.display = 'none';
            }
          });
          
          // Method 3: Hide elements by tag and position (first h1, first p after h1)
          const body = iframeDoc.body;
          if (body) {
            const firstH1 = body.querySelector('h1');
            if (firstH1 && firstH1.textContent.includes('Debug Matching Report')) {
              firstH1.style.display = 'none';
              // Hide next few siblings that might be part of the header
              // SECURITY: But keep the "Generated:" timestamp visible
              let sibling = firstH1.nextElementSibling;
              let count = 0;
              while (sibling && count < 3) {
                const tag = sibling.tagName.toLowerCase();
                if (tag === 'p' || tag === 'hr' || tag === 'div') {
                  const text = sibling.textContent || '';
                  // SECURITY: Don't hide paragraphs with "Generated:" - that's the timestamp
                  if (text.includes('Generated:')) {
                    // Keep this visible
                    sibling = sibling.nextElementSibling;
                    count++;
                    continue;
                  }
                  if (text.includes('detailed analysis') || text.includes('job matching') || text.trim().length < 100) {
                    sibling.style.display = 'none';
                  }
                }
                sibling = sibling.nextElementSibling;
                count++;
              }
            }
          }
          
          // SECURITY: Remove max-width constraint on the container to make report as wide as grey area
          // The dorset-report.html has a .container class with max-width: 1400px
          // We remove this to allow the report to use full width
          const container = iframeDoc.querySelector('.container');
          if (container) {
            container.style.maxWidth = 'none';
            container.style.width = '100%';
          }
          
          // SECURITY: Also ensure body and html take full width
          if (iframeDoc.body) {
            iframeDoc.body.style.margin = '0';
            iframeDoc.body.style.width = '100%';
          }
          if (iframeDoc.documentElement) {
            iframeDoc.documentElement.style.width = '100%';
          }
        }
      } catch (e) {
        // SECURITY: If we can't access iframe content (cross-origin), silently fail
        // This is expected behavior for security reasons
        console.log('Could not modify iframe content (may be cross-origin):', e.message);
      }
    };

    const handleError = () => {
      if (timeoutId) clearTimeout(timeoutId);
      setIsLoading(false);
      setHasError(true);
    };

    // SECURITY: Add timeout to prevent infinite loading state
    // If iframe doesn't load within 30 seconds, show error
    timeoutId = setTimeout(() => {
      setIsLoading(false);
      setHasError(true);
    }, 30000); // 30 second timeout

    const iframe = iframeRef.current;
    if (iframe) {
      iframe.addEventListener('load', handleLoad);
      iframe.addEventListener('error', handleError);
      
      // Cleanup event listeners and timeout on unmount
      return () => {
        if (timeoutId) clearTimeout(timeoutId);
        const currentIframe = iframeRef.current;
        if (currentIframe) {
          currentIframe.removeEventListener('load', handleLoad);
          currentIframe.removeEventListener('error', handleError);
        }
      };
    } else {
      // If iframe ref is not available, clear timeout
      if (timeoutId) clearTimeout(timeoutId);
    }
  }, []); // Empty dependency array - only run once on mount

  return (
    <main className={css.main}>
      <div className={css.reportWrapper}>
        {/* SECURITY: Loading indicator - shows while report is loading */}
        {isLoading && (
          <div className={css.loadingContainer}>
            <p>Loading report...</p>
          </div>
        )}

        {/* SECURITY: Error message - shows if report fails to load */}
        {hasError && (
          <div className={css.errorContainer}>
            <p className={css.errorMessage}>
              Failed to load report. Please refresh the page or contact support if the issue persists.
            </p>
          </div>
        )}

        {/* SECURITY: Iframe to embed the report HTML
            Using iframe ensures:
            1. The report's JavaScript (filtering, sorting) works correctly
            2. No CSS conflicts with React app styles
            3. The report is isolated from the React app
            4. The report can be updated independently via GitHub Actions
        */}
        <iframe
          ref={iframeRef}
          src="/dorset-report.html"
          title="Dorset Debug Matching Report"
          className={css.reportIframe}
          style={{ display: isLoading || hasError ? 'none' : 'block' }}
          // SECURITY: Sandbox attributes for additional security (optional, can be removed if needed)
          // sandbox="allow-same-origin allow-scripts"
        />
      </div>
    </main>
  );
};

export default ReportPage;
